import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ComercioActivoService } from '../services/merchant/comercio-activo.service';

/**
 * LE AGREGA EL COMERCIO ACTIVO A CADA PETICIÓN.
 *
 * 🔴 Es el mismo mecanismo que ya usa el token: un lugar, y ninguna pantalla se
 * entera. La alternativa medida era tocar 56 pantallas y 142 métodos del
 * servicio — y que en un año faltara en varias sin dar error.
 *
 * Cuatro reglas, y las tres primeras son de seguridad:
 *
 * 1. **Solo al núcleo.** Si la petición no va a `BASE_LA`, no se toca: mandarle
 *    `?merchant=` al banco o a un proveedor es filtrar información de negocio a
 *    un tercero.
 * 2. **GET y POST, siempre en la URL y nunca en el cuerpo.** En esta API muchas
 *    lecturas son POST. Como el comercio viaja en la URL, una escritura solo lo
 *    usa si su controlador decide leerlo: es una adhesión explícita. PUT, PATCH
 *    y DELETE quedan afuera — ahí el comercio va en el cuerpo o en la ruta.
 * 3. **No pisa lo explícito.** Si la pantalla ya mandó `merchant`, gana la
 *    pantalla: hay vistas que necesitan un comercio distinto del activo.
 * 4. **Sin comercio activo no agrega nada**, así que la vista "todos los
 *    comercios" devuelve exactamente lo mismo que hoy.
 */
@Injectable()
export class ComercioInterceptorService implements HttpInterceptor {

  /**
   * 🔴 GET y POST, no solo GET.
   *
   * Corregido el 2026-08-20: se hizo solo con GET por prudencia y el comercio no
   * llegaba a ninguna parte. En esta API **muchas lecturas son POST** — el
   * listado de créditos (`list_invoice_credits`), la estadística del tablero de
   * compras (`stadistic_invice_status`), la búsqueda por venta (`invoice_id`).
   * Con GET solo, el selector no cambiaba nada y parecía roto.
   *
   * Va SIEMPRE como parámetro de la URL, NUNCA en el cuerpo. Por eso es inocuo
   * en una escritura: el back solo lo usa donde el controlador decide leer
   * `req.query.merchant`, o sea que es una adhesión explícita, no un efecto
   * colateral.
   */
  private static readonly METODOS = ['GET', 'POST'];

  constructor(private comercio: ComercioActivoService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Solo en las pantallas que lo necesitan (ComercioActivoService.aplicaEn).
    // La ruta la da el servicio, no el Router: inyectar el Router en un
    // interceptor crea una dependencia circular con HttpClient.
    const codigo = this.comercio.codigoPara(this.comercio.rutaActual);

    if (!codigo) return next.handle(req);                                    // 4
    if (!ComercioInterceptorService.METODOS.includes(req.method)) return next.handle(req); // 2
    if (!environment.BASE_LA || !req.url.startsWith(environment.BASE_LA)) {  // 1
      return next.handle(req);
    }
    if (req.params.has('merchant')) return next.handle(req);                 // 3

    return next.handle(req.clone({ params: req.params.set('merchant', codigo) }));
  }
}
